import React, { useState, useEffect, useContext, useCallback } from "react";
import { Tree, Spin, message, Typography } from "antd";
import { FolderOutlined, FileTextOutlined } from "@ant-design/icons";
import usePageTitle from "../../services/usePageTitle";
import PageLayout from "../../components/PageLayout";
import HadoopToolbar from "./HadoopToolbar";
import {
  CreateDirectoryDialog,
  DeleteDirectoryDialog,
  UploadFileDialog,
} from "./HadoopDialogs";
import {
  fetchHdfsList,
  createHdfsDirectory,
  deleteHdfsDirectory,
  uploadHdfsFile,
} from "./HadoopAPI";
import { AuthContext } from "../../services/authcontext";
import hadoopHelp from "../../content/help/hadoop.json";

export default function Hadoop({ pageTitle = "Hadoop HDFS Explorer" }) {
  usePageTitle("Hadoop HDFS Explorer - SkeyeGate™");
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState(["root"]);
  const [createDirVisible, setCreateDirVisible] = useState(false);
  const [deleteDirVisible, setDeleteDirVisible] = useState(false);
  const [uploadFileVisible, setUploadFileVisible] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { user } = useContext(AuthContext);
  const isRoleUser =
    user?.roles?.some((role) => role.name === "ROLE_USER") ?? false;

  const getHdfsPrefixKeys = () => {
    const env = window._env_ || {};
    return [
      `hdfs://${window.location.hostname}:${env.HADOOP_PORT || "9000"}`,
      "hdfs://localhost:9000",
    ];
  };

  const buildTree = (nodes) => {
    const prefixes = getHdfsPrefixKeys();
    const processNode = (node) => {
      if (!node) return null;
      let cleanPath = node.path;
      for (const p of prefixes)
        if (cleanPath.startsWith(p)) {
          cleanPath = cleanPath.replace(p, "");
          break;
        }
      const paths = cleanPath.split("/").filter(Boolean);
      return {
        title: paths.length > 0 ? paths[paths.length - 1] : "root",
        key: cleanPath || "/",
        children: node.children
          ? node.children.map(processNode).filter(Boolean)
          : [],
        icon: !node.isDirectory ? (
          <FileTextOutlined style={{ color: "#1890ff" }} />
        ) : (
          <FolderOutlined style={{ color: "#faad14" }} />
        ),
        isLeaf: !node.isDirectory,
      };
    };
    const tree = (nodes || []).map(processNode).filter(Boolean);
    return tree.length
      ? tree
      : [
          {
            title: "root",
            key: "root",
            children: [],
            icon: <FolderOutlined style={{ color: "#faad14" }} />,
          },
        ];
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const files = await fetchHdfsList("/");
      const formatted = buildTree(files);
      setTreeData(formatted);
      if (formatted.length > 0) setExpandedKeys(formatted.map((n) => n.key));
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.exception ||
        error.response?.data?.message ||
        error.message ||
        "Failed to load HDFS files";
      messageApi.error({ content: msg, key: "load-hdfs-error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateDirectory = async (values) => {
    try {
      await createHdfsDirectory(values);
      messageApi.success("Directory created successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteDirectory = async (values) => {
    try {
      await deleteHdfsDirectory(values);
      messageApi.success("Directory deleted successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  const handleUploadFile = async (values) => {
    try {
      await uploadHdfsFile(values);
      messageApi.success("File uploaded successfully");
      loadData();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="hadoop-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        logo="/img/hadoop-logo-no-text"
        helpContent={hadoopHelp}
        description="Manage Hadoop cluster , allowing you to view folders and files structures, create , delete directories and upload files to hdfs."
      >
        <HadoopToolbar
          isRoleUser={isRoleUser}
          onCreateDirectory={() => setCreateDirVisible(true)}
          onDeleteDirectory={() => setDeleteDirVisible(true)}
          onUploadFile={() => setUploadFileVisible(true)}
        />
        <Spin
          spinning={loading}
          tip="Fetching Data (This Might Take a Minute)..."
        >
          <div
            style={{
              padding: "16px",
              backgroundColor: "var(--surface, #fff)",
              border: "1px solid var(--blue-100, transparent)",
              borderRadius: "8px",
            }}
          >
            {treeData.length > 0 ? (
              <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys)}
                treeData={treeData}
                defaultExpandAll
              />
            ) : (
              <Typography.Text type="secondary">
                No directories or files found.
              </Typography.Text>
            )}
          </div>
        </Spin>
      </PageLayout>
      <CreateDirectoryDialog
        open={createDirVisible}
        onClose={() => setCreateDirVisible(false)}
        onSave={handleCreateDirectory}
      />
      <DeleteDirectoryDialog
        open={deleteDirVisible}
        onClose={() => setDeleteDirVisible(false)}
        onSave={handleDeleteDirectory}
      />
      <UploadFileDialog
        open={uploadFileVisible}
        onClose={() => setUploadFileVisible(false)}
        onSave={handleUploadFile}
      />
    </div>
  );
}
